import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import { LoopOutlined } from '@mui/icons-material';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';

import { FRAUD_STATUS_LABELS } from '~/consts/order';

import { checkFraud } from '~/services/OrdersService';

import { SingleOrderPayments } from './SingleOrderPayments';
import { truncateOrderId } from './utils';

interface Props {
  order: App.Order;
  checkIn?: string;
  bookingEmail?: string;
  bookingPhone?: string;
  locationInfo?: string;
  guestFirstName?: string;
  guestLastName?: string;
  countryName?: string;
  pendingBNBL?: boolean;
}

function SingleHotelDetails({
  order,
  checkIn,
  bookingEmail = '',
  bookingPhone = '',
  locationInfo = '',
  guestFirstName = '',
  guestLastName = '',
  countryName = '',
  pendingBNBL = false,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [loadingFraudCheck, setLoadingFraudCheck] = useState<boolean>(false);
  const [recalculatedFraudScore, setRecalculatedFraudScore] = useState<number | undefined>(undefined);
  const [recalculatedFraudDesc, setRecalculatedFraudDesc] = useState<string | undefined>(undefined);

  const formattedTotal = order.total.toLocaleString(undefined, { maximumFractionDigits: 0 });

  const handleReRunFraudCheck = async () => {
    setLoadingFraudCheck(true);
    try {
      const response = await checkFraud(order.id_orders);
      setRecalculatedFraudScore(response.score);
      setRecalculatedFraudDesc(response.fraudDesc);
    } catch (error) {
      setRecalculatedFraudScore(undefined);
      setRecalculatedFraudDesc(undefined);
      enqueueSnackbar('Error fetching recalculated fraud check', { variant: 'error' });
    }
    setLoadingFraudCheck(false);
  };

  let leadDays = 999;
  if (checkIn) {
    leadDays = Math.floor(
      (new Date(checkIn).setHours(0, 0, 0, 0) - new Date(order.created_at).setHours(0, 0, 0, 0)) /
        (1000 * 60 * 60 * 24),
    );
  }
  if (pendingBNBL) {
    return (
      <div>
        Order {truncateOrderId(order.id)}{' '}
        <a target="_blank" href={`/purchases/${order.id}`} rel="noreferrer">
          BNBL order
        </a>{' '}
        for {order.currency_code}
        {formattedTotal}{' '}
        {order.fk_purchaser_id && order.fk_purchaser_id !== order.fk_customer_id && <strong>Spoofed Order</strong>}{' '}
        <Tooltip title="Fraudsters very rarely buy a BNBL.">
          <span>🟢</span>
        </Tooltip>
        <br />
        <em>{locationInfo}</em>
        <SingleOrderPayments order={order} />
      </div>
    );
  }

  const guestName = guestFirstName + ' ' + guestLastName;
  return (
    <div>
      Order {truncateOrderId(order.id)}{' '}
      <a target="_blank" href={`/purchases/${order.id}`} rel="noreferrer">
        hotel
      </a>{' '}
      for {order.currency_code}
      {formattedTotal}{' '}
      {order.fk_purchaser_id && order.fk_purchaser_id !== order.fk_customer_id && <strong>Spoofed Order</strong>}
      <br />
      <strong>Account:</strong> {order.customer_full_name} | {order.customer_email} | {order.customer_phone_prefix}
      {order.customer_phone}
      <br />
      <strong>Booking:</strong> {guestName} | {bookingEmail} | {bookingPhone}{' '}
      {order.customer_surname !== guestLastName && (
        <Tooltip title="The room is booked under a different surname. Maybe it's a friend? Or maybe it's fraud.">
          <span>🔴</span>
        </Tooltip>
      )}
      {order.customer_surname == guestLastName && (
        <Tooltip title="The room is booked under the same surname as the account. This is less likely to be fraud. (unless the fraudster recently changed the account name)">
          <span>🟢</span>
        </Tooltip>
      )}
      {order.customer_email !== bookingEmail && (
        <Tooltip title="The booking email is different from the account email.  Maybe it's a friend's email? Or maybe it's fraud.">
          <span>🟠</span>
        </Tooltip>
      )}
      {order.customer_email == bookingEmail && (
        <Tooltip title="The room is booked under the same email as the account. This is less likely to be fraud. (unless the fraudster recently changed the account email)">
          <span>🟢</span>
        </Tooltip>
      )}
      <br />
      {leadDays}d lead time{' '}
      {leadDays <= 10 && (
        <Tooltip title="Fraudsters check in quickly, before we can catch them! Booking under 10 days in advance is a risk. Under 3 days is a high risk. But, genuine customers can book last minute, too!">
          <span>{leadDays <= 3 ? '🔴' : '🟠'}</span>
        </Tooltip>
      )}
      {leadDays > 10 && (
        <Tooltip title="Fraudsters check in quickly, before we can catch them! Usually they book less than 10 days in advance. Longer lead times are more likely to be genuine customers.">
          <span>🟢</span>
        </Tooltip>
      )}
      <br />
      <em>{locationInfo}</em>
      {countryName === 'Australia' && (
        <Tooltip title="It's rare we see fraudsters booking hotels in Australia.">
          <span>🟢</span>
        </Tooltip>
      )}
      <br />
      <strong>Platform:</strong> {order.platform}
      <br />
      <strong>Fraud Status: </strong>
      {order.fraud_status ? FRAUD_STATUS_LABELS[order.fraud_status] || order.fraud_status : 'N/A'}
      <br />
      <strong>Fraud Score: </strong>
      <Tooltip title={order.fraud_desc}>
        <span>{order.fraud_score}</span>
      </Tooltip>
      <Tooltip title="Re-run fraud check">
        <IconButton size="small" disabled={loadingFraudCheck} onClick={handleReRunFraudCheck}>
          {loadingFraudCheck ? <CircularProgress size={16} /> : <LoopOutlined />}
        </IconButton>
      </Tooltip>
      <br />
      {loadingFraudCheck && <span>Loading...</span>}
      {recalculatedFraudScore !== undefined && !loadingFraudCheck && (
        <div>
          <strong>Recalculated Fraud Score: </strong>
          <Tooltip title={recalculatedFraudDesc}>
            <span>{recalculatedFraudScore}</span>
          </Tooltip>
        </div>
      )}
      <SingleOrderPayments order={order} />
    </div>
  );
}

export { SingleHotelDetails };
